"use client";

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { createPortal } from "react-dom";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { Banner } from "@astryxdesign/core/Banner";
import { Icon } from "@astryxdesign/core/Icon";
import { IconButton } from "@astryxdesign/core/IconButton";
import { researchIcons } from "@/lib/icons/research-icons";
import {
  LeadFormFields,
  type LeadFormFieldErrors,
  type LeadFormFieldState,
} from "@/components/landing/lead-form-fields";
import {
  isAgentInvokedSubmit,
  leadToolDescription,
  leadToolName,
  readLeadFormValues,
  respondToAgentSubmit,
} from "@/lib/webmcp/lead-form";
import { readBitrix24GuestTrace } from "@/lib/bitrix24/tracking";
import { readStoredUtm } from "@/lib/analytics/utm";
import { validateLeadFormValues, publicFieldsToFieldMap } from "@/lib/bitrix24/lead-form-fields";
import { DEFAULT_UI, useLeadFormConfig } from "@/lib/landing/use-lead-form-config";
import {
  formatRuPhoneInput,
  ruPhoneToE164,
  ruPhoneValidationError,
} from "@/lib/phone/ru-phone";

export type LeadRequestModalProps = {
  open: boolean;
  formCaseKey: string;
  title?: string;
  subtitle?: string;
  onClose: () => void;
};

const EMPTY: LeadFormFieldState = {
  name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
};

const ENTER_MS = 280;
const EXIT_MS = 240;
const EASE = "cubic-bezier(0.32, 0.72, 0, 1)";

function validateEmail(email: string): string | null {
  const trimmed = email.trim();
  if (!trimmed) return "Укажите email";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    return "Некорректный email";
  }
  return null;
}

export function LeadRequestModal({
  open,
  formCaseKey,
  title,
  subtitle,
  onClose,
}: LeadRequestModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(false);
  const [portalReady, setPortalReady] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);
  const [values, setValues] = useState<LeadFormFieldState>(EMPTY);
  const [errors, setErrors] = useState<LeadFormFieldErrors>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const formConfig = useLeadFormConfig(formCaseKey, open, title);
  const ui = formConfig?.ui ?? DEFAULT_UI;

  useEffect(() => {
    setPortalReady(true);
  }, []);

  useEffect(() => {
    openRef.current = mounted;
  }, [mounted]);

  const finishClose = useCallback(() => {
    setMounted(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback(() => {
    if (!openRef.current) return;
    setEntered(false);
    window.setTimeout(finishClose, EXIT_MS);
  }, [finishClose]);

  useEffect(() => {
    if (!open) {
      if (openRef.current) requestClose();
      return;
    }
    setMounted(true);
    setEntered(false);
    const frame = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setEntered(true));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, requestClose]);

  useEffect(() => {
    if (!mounted) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mounted, requestClose]);

  useEffect(() => {
    if (!entered) return;
    panelRef.current?.focus();
  }, [entered]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSuccess(null);
    setErrors({});
    setPending(false);
  }, [open, formCaseKey]);

  if (!portalReady || !mounted) return null;

  function patchValue<K extends keyof LeadFormFieldState>(
    key: K,
    value: LeadFormFieldState[K],
  ) {
    setValues((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: null }));
    }
  }

  function validateFields(next: LeadFormFieldState): LeadFormFieldErrors {
    if (formConfig?.fields) {
      return validateLeadFormValues(publicFieldsToFieldMap(formConfig.fields), next);
    }
    const nextErrors: LeadFormFieldErrors = {};
    if (ui.showName && ui.nameRequired && !next.name.trim()) {
      nextErrors.name = "Укажите имя";
    }
    if (ui.showEmail) {
      const emailErr = validateEmail(next.email);
      if (emailErr && ui.emailRequired) nextErrors.email = emailErr;
      else if (next.email.trim() && emailErr) nextErrors.email = emailErr;
    }
    if (ui.showPhone && (ui.phoneRequired || next.phone.trim())) {
      const phoneErr = ruPhoneValidationError(next.phone);
      if (phoneErr) nextErrors.phone = phoneErr;
    }
    return nextErrors;
  }

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const agent = isAgentInvokedSubmit(e);
    const raw = readLeadFormValues(e.currentTarget);
    const nextValues: LeadFormFieldState = {
      name: raw.name,
      email: raw.email,
      phone: formatRuPhoneInput(raw.phone),
      company: raw.company,
      message: raw.message,
    };
    setValues(nextValues);

    const fieldErrors = validateFields(nextValues);
    if (Object.values(fieldErrors).some(Boolean)) {
      setErrors(fieldErrors);
      return;
    }

    setPending(true);
    setError(null);
    setSuccess(null);

    const phoneE164 = ruPhoneToE164(nextValues.phone);

    const work = (async () => {
      const res = await fetch(
        `/api/public/lead-forms/${encodeURIComponent(formCaseKey)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: nextValues.name,
            email: nextValues.email,
            phone: phoneE164 || undefined,
            company: nextValues.company || undefined,
            message: nextValues.message || undefined,
            trace: readBitrix24GuestTrace(),
            utm: readStoredUtm(),
          }),
        },
      );
      const data = (await res.json().catch(() => null)) as {
        error?: string;
        message?: string;
      } | null;
      if (!res.ok) {
        throw new Error(data?.error || "Не удалось отправить заявку");
      }
      return data?.message || "Заявка отправлена";
    })();

    if (agent) {
      respondToAgentSubmit(
        e,
        work.then(
          (msg) => msg,
          (err) =>
            err instanceof Error ? err.message : "Не удалось отправить заявку",
        ),
      );
    }

    try {
      const msg = await work;
      setSuccess(msg);
      setValues(EMPTY);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ошибка");
    } finally {
      setPending(false);
    }
  }

  const heading = formConfig?.title || title || "Оставить заявку";
  const caption =
    subtitle || "Оставьте контакты — вернёмся с предложением в рабочие часы.";

  const motionMs = entered ? ENTER_MS : EXIT_MS;

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-end justify-center p-4 sm:items-center sm:p-6"
      role="presentation"
      onClick={requestClose}
    >
      <div
        className="absolute inset-0 bg-black/55 backdrop-blur-[6px]"
        aria-hidden
        style={{
          opacity: entered ? 1 : 0,
          transition: `opacity ${motionMs}ms ${EASE}`,
        }}
      />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative w-full max-w-lg overflow-hidden rounded-xl border border-border bg-[var(--color-background-body)] shadow-2xl outline-none will-change-transform"
        style={{
          opacity: entered ? 1 : 0,
          transform: entered
            ? "translateY(0) scale(1)"
            : "translateY(14px) scale(0.98)",
          transition: `opacity ${motionMs}ms ${EASE}, transform ${motionMs}ms ${EASE}`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-5 py-5 sm:px-6">
          <HStack justify="between" vAlign="start" width="100%" gap={3}>
            <HStack gap={3} vAlign="start" className="min-w-0 flex-1">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted text-primary">
                <Icon icon={researchIcons.messageCircleQuestion} size="md" />
              </div>
              <VStack gap={1} align="start" className="min-w-0">
                <Heading
                  level={2}
                  id={titleId}
                  type="display-3"
                  className="text-balance"
                >
                  {heading}
                </Heading>
                <Text color="secondary" size="sm" className="text-pretty">
                  {caption}
                </Text>
              </VStack>
            </HStack>
            <IconButton
              label="Закрыть"
              variant="ghost"
              size="sm"
              onClick={requestClose}
              icon={<Icon icon={researchIcons.close} size="sm" />}
              className="shrink-0 rounded-md"
            />
          </HStack>
        </div>

        <div className="max-h-[min(70dvh,36rem)] overflow-y-auto px-5 py-5 sm:px-6">
          {error ? (
            <Banner status="error" title={error} container="card" />
          ) : null}

          {success ? (
            <VStack gap={4} align="center" width="100%" className="py-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-500">
                <Icon icon={researchIcons.checkCircle} size="lg" />
              </div>
              <VStack gap={1} align="center">
                <Text weight="semibold">
                  Заявка отправлена
                </Text>
                <Text color="secondary" size="sm">
                  {success}
                </Text>
              </VStack>
              <Button
                label="Отлично"
                variant="primary"
                size="lg"
                onClick={requestClose}
                className="w-full sm:w-auto sm:min-w-[12rem]"
              />
            </VStack>
          ) : (
            <form
              className="w-full"
              toolname={leadToolName(formCaseKey)}
              tooldescription={leadToolDescription(heading)}
              onSubmit={(ev) => void submit(ev)}
            >
              <VStack gap={4} width="100%">
                <LeadFormFields
                  values={values}
                  onChange={patchValue}
                  errors={errors}
                  showName={ui.showName}
                  showEmail={ui.showEmail}
                  showPhone={ui.showPhone}
                  showCompany={ui.showCompany}
                  showMessage={ui.showMessage}
                  nameRequired={ui.nameRequired}
                  emailRequired={ui.emailRequired}
                  phoneRequired={ui.phoneRequired}
                  companyRequired={ui.companyRequired}
                  messageRequired={ui.messageRequired}
                  hasAutoFocus
                  onPhoneBlur={() => {
                    setErrors((prev) => ({
                      ...prev,
                      phone: ruPhoneValidationError(values.phone),
                    }));
                  }}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  label={
                    pending ? "Отправка…" : formConfig?.submitLabel || "Отправить"
                  }
                  isLoading={pending}
                  isDisabled={pending}
                  className="w-full"
                />
              </VStack>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
